import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';
import Header from '../../components/layout/Header';
import {
  Card,
  CardContent,
  CardFooter,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft } from 'lucide-react';
import TopicsList from '../../components/modules/TopicsList';
import BatchesList from '../../components/modules/BatchesList';
import RecordingsList from '../../components/modules/RecordingsList';
import MaterialsList from '../../components/modules/MaterialsList';
import AssignmentsList from '../../components/modules/AssignmentsList';
import { app } from "../../firebase";
import { getAuth } from "firebase/auth";

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  FirestoreError,
  DocumentReference,
  getFirestore,
  collection,
  getDocs,
} from 'firebase/firestore';
import { convertDBTopicsMultiple, convertModuleToUITopicsSingle, convertToDBModuleTopicsForSingle, transformBackFormatDBMultiple } from '../../utils/DataOperations';
import { useLoading } from '../../hooks/useLoading';

// Types for the module form and data  
type Topic = {
  id: string;
  title: string;
};

type TopicChip = {
  id: string;
  text: string;
};

type BatchAssignment = {
  id?: string;
  batchId: string;
  isCompleted: boolean;
};

type Recording = {
  id: string;
  video: string;
  batchId: string;
};


type Material = {
  id?: string;
  fileName: string;
  link: string;
  file?: File;
}

type ModuleAssignment = {
  id: string;
  title: string;
  description?: string;
  dueDate?: Date; // Adjust based on your application  
  file?: File;
  fileLink?: string;
};

type ModuleData = {
  id?: string;
  moduleName: string;
  moduleDescription: string;
  topics: Topic[];
  topicChips: TopicChip[];
  batches: any[];
  recordings: any[];
  materials: any[];
  tasks: any[];
  moduleTopics?: any[]
};

const AddEditModule = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const courseId = queryParams.get('courseId');
  const isEdit = !!moduleId;
  const db = getFirestore(app);
  const auth = getAuth(app);
  const [batches, setBatches] = useState([]);
  const { setIsLoading } = useLoading();
  const [isLoading, setISLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [moduleData, setModuleData] = useState<ModuleData>({
    moduleName: '',
    moduleDescription: '',
    topics: [],
    topicChips: [],
    batches: [],
    recordings: [],
    materials: [],
    tasks: [],
  });

  // Fetch module data if in edit mode  
  useEffect(() => {
    window.scrollTo(0,0);
    if (isEdit && moduleId) {
      setLoading(true);
      setIsLoading(true);
      const fetchModuleData = async () => {
        try {
          const moduleRef: DocumentReference = doc(db, 'modules', moduleId);
          const docSnap = await getDoc(moduleRef);

          if (docSnap.exists()) {
            let moduleData: any = { id: moduleId, ...docSnap.data() };
            moduleData.topics = !(Array.isArray(moduleData?.moduleTopics)) ?
              convertModuleToUITopicsSingle(moduleData?.moduleTopics) :
              transformBackFormatDBMultiple(moduleData?.moduleTopics)
            moduleData.recordings = moduleData?.recordings?.map((x: any, index: any) => {
              return { ...x, id: x.hasOwnProperty("id") ? x.id : Date.now().toString() + index }
            }) ?? [];
            setModuleData(moduleData as ModuleData);
            setIsLoading(false);
          } else {
            toast({
              title: "Error",
              description: "Module not found",
              variant: "destructive",
            });
          }
        } catch (error: any) {
          setIsLoading(false);
          if (error instanceof FirestoreError) {
            toast({
              title: "Error",
              description: "Failed to fetch module data.",
              variant: "destructive",
            });
          }
        } finally {
          setIsLoading(false);
          setLoading(false);
        }
      };

      const fetchBatchesData = async () => {
        setLoading(true); // Start loading before fetching data  

        try {
          const batchesCollection = collection(db, 'courseBatches'); // Reference the collection  
          const querySnapshot = await getDocs(batchesCollection); // Get all documents in the collection  

          // Map the documents to an array  
          const batchesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),

          }));

          setBatches(batchesData.sort(sortByBatchName)); // Update state with batches  
        } catch (error: any) {
          if (error instanceof FirestoreError) {
            toast({
              title: "Error",
              description: "Failed to fetch batch data.",
              variant: "destructive",
            });
          }
        } finally {
          setLoading(false); // Stop loading after the attempt  
        }
      };

      fetchModuleData();
      fetchBatchesData();
    }
  }, [isEdit, moduleId]);

  const sortByBatchName = (a: any, b: any) => {
    if (a.batchName < b.batchName) {
      return -1; // a should come before b  
    }
    if (a.batchName > b.batchName) {
      return 1; // a should come after b  
    }
    return 0; // a and b are equal  
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setModuleData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTopicsChange = (topics: Topic[]) => {
    setModuleData((prev) => ({ ...prev, topics }));
  };

  const handleTopicChipsChange = (topicChips: TopicChip[]) => {
    setModuleData((prev) => ({ ...prev, topicChips }));
  };

  const handleBatchAssignmentsChange = (batches: BatchAssignment[]) => {
    setModuleData((prev) => ({ ...prev, batches }));
  };

  const handleRecordingsChange = (recordings: Recording[]) => {
    setModuleData((prev) => ({ ...prev, recordings }));
  };

  const handleMaterialsChange = (materials: Material[]) => {
    setModuleData((prev) => ({ ...prev, materials }));
  };

  const handleAssignmentsChange = (tasks: any[]) => {
    setModuleData((prev) => ({ ...prev, tasks }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    moduleData.moduleTopics =
      moduleData?.topics?.length > 1 ? convertDBTopicsMultiple(moduleData)?.moduleTopics :
        convertToDBModuleTopicsForSingle(moduleData?.topics)?.moduleTopics;


    if (!moduleData.moduleName.trim()) {
      toast({
        title: "Error",
        description: "Module name is required",
        variant: "destructive",
      });
      return;
    }

    let moduleDataBody = {
      ...JSON.parse(JSON.stringify(moduleData))
    }

    moduleDataBody?.tasks?.forEach(x => {
      if (x.link.toString().lastIndexOf('pdfs') == -1 && x.link.toString().lastIndexOf('assets') == -1) {
        x.link = `../../assets/pdfs/${x.link}`
      }
    });

    moduleDataBody?.materials?.forEach(x => {
      if (x.link.toString().lastIndexOf('pdfs') == -1 && x.link.toString().lastIndexOf('assets') == -1) {
        x.link = `../../assets/pdfs/${x.link}`
      }
    });

    delete moduleDataBody['topics'];

    setLoading(false);

    try {
      if (isEdit && moduleId) {
        const moduleRef = doc(db, 'modules', moduleId);
        await setDoc(moduleRef, moduleDataBody);
        toast({
          title: isEdit ? "Module Updated" : "Module Created",
          description: `${moduleData.moduleName} has been ${isEdit ? 'updated' : 'created'} successfully.`,
        });
      }
      else {
        const moduleRef = doc(collection(db, "modules"));

        // Add the generated ID to the course data object  
        const moduleDataWithId = {
          id: moduleRef.id, // Assign the generated ID  
          moduleName: moduleDataBody['moduleName'] ?? moduleDataBody.moduleName,
          description: moduleDataBody['moduleDescription'] ?? moduleDataBody.moduleDescription,
        };

        const courseRef = doc(db, 'courses', courseId);
        const courseData = await getDoc(courseRef);

        if (courseData.exists()) {
          const course = courseData.data();

          if (course?.sub) {
            
          }
        }
        // Save the course data to Firestore with the generated ID  
        await setDoc(moduleRef, moduleDataWithId);

        toast({
          title: "Module created",
          description: `${moduleDataBody.moduleName} has been successfully created`,
        });
        navigate(`/courses/edit/${courseId}`);
      }
     
    } catch (error: any) {
      if (error instanceof FirestoreError) {
        toast({
          title: "Error",
          description: "Failed to save module data.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <Header
        title={isEdit ? "Edit Module" : "Add New Module"}
        subtitle={isEdit ? `Editing: ${moduleData.moduleName}` : "Create a new module for your course"}
      />

      <div className="flex-1 p-4 md:p-8 animate-fade-in overflow-auto">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate(`/courses/edit/${courseId}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Course
        </Button>

        <form onSubmit={handleSubmit}>
          <Card className="max-w-5xl mx-auto">
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-medium">
                    Module Name<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="moduleName"
                    value={moduleData.moduleName}
                    onChange={handleInputChange}
                    placeholder="e.g., Introduction to Web Development"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-base font-medium">
                    Module Description<span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="moduleDescription"
                    name="moduleDescription"
                    value={moduleData.moduleDescription}
                    onChange={handleInputChange}
                    placeholder="Provide a detailed description of the module"
                    className="min-h-[100px]"
                    required
                  />
                </div>
              </div>

              <TopicsList
                topics={moduleData?.topics ?? []}
                onTopicsChange={handleTopicsChange}
              />

              <BatchesList
                batches={moduleData?.batches ?? []}
                availableBatches={batches ?? []}
                onBatchAssignmentsChange={handleBatchAssignmentsChange}
              />

              <RecordingsList
                recordings={moduleData?.recordings ?? []}
                availableBatches={batches ?? []}
                onRecordingsChange={handleRecordingsChange}
              />

              <AssignmentsList
                tasks={moduleData?.tasks ?? []}
                onAssignmentsChange={handleAssignmentsChange}
              />

              <MaterialsList
                materials={moduleData?.materials ?? []}
                onMaterialsChange={handleMaterialsChange}
              />
            </CardContent>

            <CardFooter className="flex justify-between p-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/courses/edit/${courseId}`)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Module' : 'Save Module'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default AddEditModule;